import type { ReadTransaction, WriteTransaction } from 'replicache'
import { commitMutators } from '../commit/mutators'
import {
  BlockRelationship,
  blockRelationshipBaseOps,
  Branch,
  branchBaseOps,
  BranchLineage,
  GenericNotification,
  getBRId,
  isBranch,
  isCommit,
  NotificationSubscription,
  RootBlock,
  slugifyBranchName,
  isPlayground,
  Playground,
} from '@basehub/data/replicache/index'
import { WithServerSecret } from '../../../tx/spec'
import { contentRequestMutators } from '../content-request/mutators'
import { getIdempotentId } from '@basehub/utils/id/index'
import { invariant } from '@epic-web/invariant'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { serverAuthorizedOrThrow } from '../../../tx/server-auth'
import { suggestedBranchMutators } from '../suggested-branch/mutators'
import { SetOptional, SetRequired } from 'type-fest'
import { hasAccessOrThrow } from '../../team-space/team/auth'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { idToSourceBlockId } from '../block/mutators/copy-on-write'
import {
  commentExtensionsForTransformations,
  plainTextToYDocBase64State,
} from '@basehub/tiptap-api-utils'

export const RECENTLY_CREATED_BRANCH_STORAGE_KEY = `bshb_recently_created_branch`

/**
 * Gets the branch or the commit depending on the query parameter passed
 */
export async function getBranchOrCommit(
  tx: ReadTransaction,
  {
    branchOrCommit,
    fallbackToDefault = true,
  }: { branchOrCommit: string | null; fallbackToDefault?: boolean }
) {
  if (!branchOrCommit) {
    if (!fallbackToDefault) return null
    // just use default branch
    const branches = await branchMutators.listBranches(tx)
    const defaultBranch = branches.find((b) => b.isDefault)
    if (!defaultBranch) return null
    return { result: defaultBranch, isFallback: true }
  }
  const [foundCommit, branches] = await Promise.all([
    commitMutators.getCommit(tx, branchOrCommit),
    branchMutators.listBranches(tx),
  ])
  const foundBranch = branches.find((b) => {
    return b.name === branchOrCommit || b.id === branchOrCommit
  })
  // branch name can't be equal to a commit id
  if (foundBranch) return { result: foundBranch, isFallback: false }
  if (foundCommit) return { result: foundCommit, isFallback: false }
  if (!fallbackToDefault) return null
  // if we didn't find a branch or commit, just use default branch
  const defaultBranch = branches.find((b) => b.isDefault)
  if (!defaultBranch) return null
  return { result: defaultBranch, isFallback: true }
}

export type CreateBranchParams = WithServerSecret<{
  data: Pick<
    SetRequired<Branch, 'authorId'>,
    | 'id'
    | 'createdAt'
    | 'name'
    | 'isDefault'
    | 'git'
    | 'authorId'
    | 'description'
    | 'isInlineSuggestion'
    | 'playgroundId'
  >
  baseBranchOrCommitId: string
  idSeed: string
}>

export async function createBranch_DONT_USE_DIRECTLY(
  tx: WriteTransaction,
  { data, baseBranchOrCommitId, idSeed }: CreateBranchParams
) {
  console.log('[createBranch_DONT_USE_DIRECTLY mutator] Starting branch creation', {
    branchName: data.name,
    branchId: data.id,
    baseBranchOrCommitId,
    idSeed,
    authorId: data.authorId,
    gitBranch: data.git?.branch,
    isDefault: data.isDefault,
    playgroundId: data.playgroundId,
  })

  ensureBaseHubTransaction(tx)

  const newWorkingRootId = idSeed
  const newBranchName = slugifyBranchName(data.name)

  console.log('[createBranch_DONT_USE_DIRECTLY mutator] Branch name slugified', {
    originalName: data.name,
    slugifiedName: newBranchName,
    newWorkingRootId,
  })

  invariant(newBranchName, 'Branch name can not be empty')
  invariant(
    newBranchName.length <= 250,
    'Branch name needs to be less than 250 characters'
  )

  const baseBranchOrCommit = await getBranchOrCommit(tx, {
    branchOrCommit: baseBranchOrCommitId,
    fallbackToDefault: false,
  })

  console.log('[createBranch_DONT_USE_DIRECTLY mutator] Fetched base branch or commit', {
    baseBranchOrCommitId,
    found: !!baseBranchOrCommit,
    resultType: baseBranchOrCommit?.result
      ? isCommit(baseBranchOrCommit.result)
        ? 'commit'
        : 'branch'
      : 'none',
    resultId: baseBranchOrCommit?.result?.id,
  })

  invariant(baseBranchOrCommit, 'Base branch or commit not found')

  const baseBranchLineage = isCommit(baseBranchOrCommit.result)
    ? baseBranchOrCommit.result.branchLineage
    : baseBranchOrCommit.result.lineage
  const baseBranchId = isCommit(baseBranchOrCommit.result)
    ? baseBranchOrCommit.result.branchId
    : baseBranchOrCommit.result.id
  const headCommit = isCommit(baseBranchOrCommit.result)
    ? baseBranchOrCommit.result
    : baseBranchOrCommit.result.headCommit
  const headCommitRootBlockId = headCommit?.rootBlockId
  const headCommitRootBlock = headCommitRootBlockId
    ? await blockBaseOps.get(tx, headCommitRootBlockId)
    : null

  console.log('[createBranch_DONT_USE_DIRECTLY mutator] Fetching base branch data', {
    baseBranchId,
    headCommitId: headCommit?.id,
    headCommitRootBlockId,
  })

  const baseBranchData = await branchBaseOps
    .get(tx, baseBranchId)
    .then(async (b) => {
      if (!b) throw new Error(`Base branch ${baseBranchId} not found`)
      invariant(
        b.workingRootBlockId,
        `workingRootBlockId not found on base branch ${baseBranchId}`
      )
      const baseBranchWorkingRootBlock = await blockBaseOps.get(
        tx,
        b.workingRootBlockId
      )
      if (!baseBranchWorkingRootBlock) {
        throw new Error(`Working root block ${b.workingRootBlockId} not found`)
      }
      return { branch: b, workingRootBlock: baseBranchWorkingRootBlock }
    })
  invariant(baseBranchData, `Base branch ${baseBranchId} not found`)
  invariant(
    !data.playgroundId || baseBranchData.branch.isDefault,
    'Can not create a playground branch from a non-default branch'
  )

  console.log('[createBranch_DONT_USE_DIRECTLY mutator] Base branch data fetched', {
    baseBranchId,
    baseBranchName: baseBranchData.branch.name,
    baseBranchWorkingRootBlockId: baseBranchData.branch.workingRootBlockId,
  })

  const newBranchLineage: BranchLineage = [
    {
      branchId: baseBranchId,
      checkoutAt: data.createdAt,
      lastSyncedAt: data.createdAt,
    },
    ...(baseBranchLineage ?? []),
  ]

  const workingRootBlock: RootBlock = headCommitRootBlock
    ? {
        ...(headCommitRootBlock as RootBlock),
        isCommitted: false,
        createdAt: data.createdAt,
        updatedAt: data.createdAt,
        id: newWorkingRootId,
        branchId: data.id,
        branchLineage: newBranchLineage,
        sourceBlockId: idToSourceBlockId(baseBranchData.workingRootBlock),
        isInlineSuggestion: data.isInlineSuggestion,
      }
    : {
        id: newWorkingRootId,
        title: 'root',
        type: 'root',
        value: {},
        constraints: {},
        createdAt: data.createdAt,
        typeOptions: {},
        isCommitted: false,
        hidden: false,
        sourceBlockId: idToSourceBlockId(baseBranchData.workingRootBlock),
        searchConfig: { index: false },
        branchId: data.id,
        branchLineage: newBranchLineage,
        isInlineSuggestion: data.isInlineSuggestion,
      }

  if (data.playgroundId) {
    workingRootBlock.playgroundId = data.playgroundId
  }

  const [branches, suggestedBranchToResolve] = await Promise.all([
    branchMutators.listBranches(tx),
    data.git?.branch && !data.playgroundId
      ? suggestedBranchMutators
          .listSuggestedBranches(tx)
          .then((suggestedBranches) => {
            return suggestedBranches.find(
              (sb) => sb.git?.branch === data.git?.branch
            )
          })
      : Promise.resolve(),
  ])

  if (data.isDefault) {
    // check if there is already a default branch
    const defaultBranch = branches.find((b) => b.isDefault)
    if (defaultBranch) {
      throw new Error('default branch already exists')
    }
  }

  console.log('[createBranch_DONT_USE_DIRECTLY mutator] Validating branch name uniqueness', {
    newBranchName,
    existingBranchCount: branches.length,
    gitBranch: data.git?.branch,
  })

  const branchNameIsRepeated = branches.some((b) => b.name === newBranchName)
  if (branchNameIsRepeated) {
    console.error('[createBranch_DONT_USE_DIRECTLY mutator] Branch name already exists', {
      newBranchName,
    })
    throw new MutatorUserError('A branch with this name already exists')
  }
  if (data.git?.branch) {
    const gitBranchNameIsRepeated = branches.some(
      (b) => b.git?.branch === data.git?.branch
    )
    if (gitBranchNameIsRepeated) {
      console.error('[createBranch_DONT_USE_DIRECTLY mutator] Git branch already linked', {
        gitBranch: data.git?.branch,
      })
      throw new MutatorUserError(
        'A branch linked to this git branch already exists'
      )
    }
  }

  console.log('[createBranch_DONT_USE_DIRECTLY mutator] Validation passed, creating branch')

  const firstCRId = (idSeed = getIdempotentId({ idSeed }))

  const branch: SetRequired<Branch, 'authorId'> = {
    ...data,
    name: newBranchName,
    headCommit,
    headCommitId: headCommit?.id ?? null,
    rollback: null,
    workingRootBlockId: newWorkingRootId,
    sourceBranchId: baseBranchId,
    contentRequestIds: [firstCRId],
    updatedAt: data.createdAt,
    lineage: newBranchLineage,
    description:
      data.playgroundId && !data.description
        ? (
            await plainTextToYDocBase64State(
              'This is a playground branch. You can edit everything, play with it, see how it feels. Enjoy!',
              commentExtensionsForTransformations
            )
          ).base64
        : data.description,
  }

  const workingRootBR: BlockRelationship = {
    branchId: branch.id,
    id: getBRId(newWorkingRootId, newWorkingRootId),
    validFrom: branch.createdAt,
    sourceBlockId: idToSourceBlockId(workingRootBlock),
    blockId: newWorkingRootId,
    parentId: '',
    isCommitted: false,
    order: 'a0',
    structural: true,
  }
  if (data.playgroundId) {
    workingRootBR.playgroundId = data.playgroundId
  }

  console.log('[createBranch_DONT_USE_DIRECTLY mutator] Persisting branch and related entities', {
    branchId: branch.id,
    branchName: branch.name,
    workingRootBlockId: newWorkingRootId,
    contentRequestId: firstCRId,
    suggestedBranchToResolve: suggestedBranchToResolve?.id,
  })

  await Promise.all([
    branchBaseOps.put(tx, branch),
    blockBaseOps.put(tx, workingRootBlock, null),
    blockRelationshipBaseOps.set(tx, workingRootBR),
    contentRequestMutators.createContentRequest(tx, {
      title: branch.name,
      headBranchId: branch.id,
      baseBranchId,
      authorId: branch.authorId,
      createdAt: branch.createdAt,
      description: null,
      id: firstCRId,
      ...(branch.playgroundId ? { playgroundId: branch.playgroundId } : {}),
    }),
    suggestedBranchToResolve
      ? suggestedBranchMutators.resolveSuggestedBranch(tx, {
          id: suggestedBranchToResolve.id,
          resolution: { type: 'accepted', branchId: branch.id },
          resolvedAt: branch.createdAt,
        })
      : Promise.resolve(),
  ])

  console.log('[createBranch_DONT_USE_DIRECTLY mutator] Branch created successfully', {
    branchId: branch.id,
    branchName: branch.name,
    workingRootBlockId: branch.workingRootBlockId,
    headCommitId: branch.headCommitId,
    sourceBranchId: branch.sourceBranchId,
    contentRequestIds: branch.contentRequestIds,
  })

  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(
      RECENTLY_CREATED_BRANCH_STORAGE_KEY,
      branch.name
    )
  }

  return { branch, baseBranchOrCommit: baseBranchOrCommit.result }
}

export async function updateBranch(
  tx: WriteTransaction,
  {
    id,
    isoNow,
    data,
  }: WithServerSecret<{
    id: Branch['id']
    isoNow: string
    data: Partial<
      Omit<
        Branch,
        'id' | 'createdAt' | 'archivedAt' | 'workingRootBlockId' | 'isDefault'
      >
    >
  }>
) {
  ensureBaseHubTransaction(tx)

  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: 'branch-author',
  })

  const branch = await branchBaseOps.get(tx, id)
  invariant(branch && isBranch(branch), 'Branch not found')

  const branches = await branchMutators.listBranches(tx)

  if (data.name) {
    const nameExists = branches.some((b) => b.id !== id && b.name === data.name)
    if (nameExists) {
      throw new MutatorUserError('A branch with this name already exists')
    }
  }

  if (data.git?.branch) {
    let gitBranchExists = branches.find(
      (b) => b.id !== id && b.git?.branch === data.git?.branch
    )
    if (gitBranchExists && gitBranchExists.archivedAt) {
      await branchBaseOps.put(tx, {
        ...gitBranchExists,
        git: { ...gitBranchExists.git, branch: '' },
      })
      gitBranchExists = undefined
    }
    if (gitBranchExists) {
      throw new MutatorUserError(
        'A branch linked to this git branch already exists'
      )
    } else {
      await suggestedBranchMutators.deleteSuggestedBranchByName(tx, {
        name: data.git.branch,
      })
    }
  }

  const updatedBranch: Branch = {
    ...branch,
    ...data,
    updatedAt: isoNow,
  }

  await branchBaseOps.put(tx, updatedBranch)

  return updatedBranch
}

export async function deleteBranch_DONT_USE_DIRECTLY(
  tx: WriteTransaction,
  { id }: WithServerSecret<{ id: Branch['id'] }>
) {
  ensureBaseHubTransaction(tx)

  const playgroundId =
    tx.serverOnly?.playgroundId ?? tx.clientOnly?.playgroundId

  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: 'branch-author',
  })

  const branch = await branchBaseOps.get(tx, id)
  invariant(branch && isBranch(branch), 'Branch not found')
  invariant(!branch.isDefault, 'Cannot delete default branch')
  invariant(isPlayground(branch), 'Can only delete playground branches')
  invariant(playgroundId === branch.playgroundId, 'Playground ID mismatch')

  await branchBaseOps.delete(tx, id)
  return id
}

export async function makeDefaultBranch(
  tx: WriteTransaction,
  { id, isoNow }: WithServerSecret<{ id: Branch['id']; isoNow: string }>
) {
  ensureBaseHubTransaction(tx)

  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: 'branch-author',
  })

  let branch: Branch | undefined
  let defaultBranch: Branch | undefined

  const branches = await branchMutators.listBranches(tx)
  branches.forEach((b) => {
    if (b.isDefault) {
      defaultBranch = b
    }
    if (b.id === id) {
      branch = b
    }
  })

  invariant(branch, `Branch with id ${id} not found`)

  if (branch.isDefault) return branch

  if (defaultBranch) {
    await branchBaseOps.put(tx, {
      ...defaultBranch,
      isDefault: false,
      updatedAt: isoNow,
    })
  }

  await branchBaseOps.put(tx, {
    ...branch,
    isDefault: true,
    updatedAt: isoNow,
  })

  return branch
}

export async function toggleArchiveBranch(
  tx: WriteTransaction,
  {
    id,
    archivedAt,
    archivedBy,
    archivedByUsername,
    triggerNotification = true,
  }: WithServerSecret<{
    id: Branch['id']
    archivedAt: string
    archivedBy: string
    archivedByUsername: string
    triggerNotification?: boolean
  }>
) {
  ensureBaseHubTransaction(tx)

  // ensure user is owner or member
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: 'branch-author',
  })

  const branch = await branchMutators.getBranch(tx, id)
  invariant(branch && isBranch(branch), 'Branch not found')

  if (branch.isDefault) {
    throw new Error('Cannot archive default branch')
  }

  const _archivedAt = branch.archivedAt ? null : archivedAt
  const _archivedBy = branch.archivedAt ? null : archivedBy
  if (_archivedAt) {
    invariant(!!_archivedBy, 'archivedBy not found while archiving branch')
  }

  // archived branch names should like `branchName-archivedAtTimestamp`
  const timestampRegex = /\d{13}$/
  const branchNameWithoutTimestamp = timestampRegex.test(branch.name)
    ? branch.name.split('-').slice(0, -1).join('-')
    : branch.name
  const newBranchName = _archivedAt
    ? `${branchNameWithoutTimestamp}-${new Date(_archivedAt).getTime()}`
    : branch.name
  const updates: Partial<Branch> = {
    archivedAt: _archivedAt,
    archivedBy: _archivedBy,
    name: newBranchName,
    ...(_archivedAt ? { updateBranch: _archivedAt } : {}),
  }

  await branchBaseOps.update(tx, { id, ...updates })
  if (tx.serverOnly && triggerNotification && updates.archivedAt) {
    const notificationSubscription: SetOptional<
      NotificationSubscription,
      'id' | 'deletedAt'
    > = {
      type: 'branch-events',
      branchId: branch.id,
      userIds: [archivedBy],
    }
    // if is archived create a notification
    const notificationToSend: GenericNotification = {
      authorId: archivedBy,
      type: 'repo-cr-resolution',
      resolution: 'archived',
      authorUsername: archivedByUsername,
      createdAt: archivedAt,
      href: `/${tx.serverOnly.team?.slug}/${tx.serverOnly.repo?.slug}/branch/${branch.name}`,
      read: false,
      branchName: branch.name,
      branchDescription: branch.description,
      branchId: branch.id,
    }
    return {
      ...branch,
      ...updates,
      notificationToSend,
      notificationSubscription,
    }
  }
  return {
    ...branch,
    ...updates,
    notificationToSend: null,
    notificationSubscription: null,
  }
}

export async function rollbackBranch(
  tx: WriteTransaction,
  {
    branchId,
    commitId,
    isoDate,
    serverSecret,
  }: WithServerSecret<{
    branchId: string
    commitId: string | null
    isoDate: string
  }>
) {
  const isDisabling = commitId === null
  console.log('[rollbackBranch mutator] Starting rollbackBranch', {
    branchId,
    commitId,
    isoDate,
    isDisabling,
  })

  ensureBaseHubTransaction(tx)
  serverAuthorizedOrThrow(serverSecret)

  // ensure user is owner or member
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: 'branch-author',
  })

  console.log('[rollbackBranch mutator] Access check passed', { isDisabling })

  const branch = await branchMutators.getBranch(tx, branchId)
  invariant(branch, `branch ${branchId} not found`)

  console.log('[rollbackBranch mutator] Fetched branch', {
    branchId,
    branchName: branch.name,
    currentRollback: branch.rollback,
    headCommitId: branch.headCommitId,
    isDisabling,
  })

  // If null, we'll remove the rollbackCommitId and use the headCommit again
  const commitIdToFetch =
    commitId ?? ((branch.headCommitId ?? branch.headCommit?.id) as string)

  console.log('[rollbackBranch mutator] Fetching commit', {
    commitIdToFetch,
    isDisabling,
  })

  const commit = await commitMutators.getCommit(tx, commitIdToFetch)
  invariant(commit, `commit ${commitId} not found`)

  console.log('[rollbackBranch mutator] Fetched commit', {
    commitId: commit.id,
    commitRootBlockId: commit.rootBlockId,
    isDisabling,
  })

  const rollback = commitId
    ? { commitId, isoDate, rootBlockId: commit.rootBlockId }
    : null
  const updates: Partial<Branch> = { rollback, updatedAt: isoDate }

  console.log('[rollbackBranch mutator] Preparing branch update', {
    rollback,
    updates,
    isDisabling,
  })

  await branchBaseOps.update(tx, { id: branchId, ...updates })

  console.log('[rollbackBranch mutator] Branch updated successfully', {
    branchId,
    newRollback: rollback,
    isDisabling,
  })

  return { branch: { ...branch, ...updates }, commit }
}

const setPlaygroundExpiresAt = async (
  tx: WriteTransaction,
  {
    branchId,
    sourceBranchId,
    expiresAt,
    isoNow,
  }: {
    branchId: string
    sourceBranchId: string
    expiresAt: string
    isoNow: string
  }
): Promise<{ playground: Playground; sourceBranch: Branch }> => {
  ensureBaseHubTransaction(tx)

  const [branch, sourceBranch] = await Promise.all([
    branchBaseOps.get(tx, branchId),
    branchBaseOps.get(tx, sourceBranchId),
  ])
  invariant(branch, `Branch ${branchId} not found`)
  invariant(sourceBranch, `Source branch ${sourceBranchId} not found`)

  if (!isPlayground(branch)) {
    throw new MutatorUserError(
      'Can not set expiresAt on a non-playground branch'
    )
  }

  if (branch.playgroundExpiresAt) {
    throw new MutatorUserError(
      'Playground branch already has an expiration date set'
    )
  }

  const updatedBranch: Playground = {
    ...branch,
    playgroundExpiresAt: expiresAt,
    updatedAt: isoNow,
  }

  await branchBaseOps.update(tx, updatedBranch)

  return { playground: updatedBranch, sourceBranch }
}

export const branchMutators = {
  getBranch: branchBaseOps.get,
  listBranches: branchBaseOps.list,
  getBranchOrCommit,
  createBranch_DONT_USE_DIRECTLY,
  deleteBranch_DONT_USE_DIRECTLY,
  updateBranch,
  toggleArchiveBranch,
  rollbackBranch,
  setPlaygroundExpiresAt,
}
